/**
 * The event page's head and JSON-LD (WHO-OWNS-WHICH-URL.md §4). Same shape as lib/head.js's buildHead, so
 * components/EventsHead.js renders either one, and scripts/events-route.js bakes either one.
 *
 * Everything comes from the payload the catalog already returns: `seo.title`/`seo.description`, `images.og`
 * (falling back to the hero, then the site card), one schema.org Event per entry in `event_sessions`, and a
 * BreadcrumbList. Canonical is the page's own URL, built from SITE_ORIGIN, never hardcoded.
 */
import { LANDING, RELEASED } from "./head";
import { eventJsonLd, SITE_ORIGIN } from "./jsonld";

export const eventPath = (slug) => `/events/${slug}`;

/** og:image, twitter:image and JSON-LD `image` are read off-origin, so a site-relative media path (K-18) has to
 *  be absolute or the crawler cannot resolve it — a relative og:image is simply no share card at all. */
const absolute = (url, siteOrigin) => (typeof url === "string" && url.startsWith("/") ? `${siteOrigin}${url}` : url);
export const eventUrl = (slug, siteOrigin = SITE_ORIGIN) => `${siteOrigin}${eventPath(slug)}`;

/** Home › Events › {title}, so the crawler sees where the page sits. */
/** The neutral card: a wordmark, not another event's photograph. */
export const fallbackCard = (siteOrigin = SITE_ORIGIN) => ({
  url: `${siteOrigin}/events-assets/events-card-og.jpg`,
  alt: "KGOB Events — CPAs & Advisors, Charlotte, NC",
});

export function breadcrumbJsonLd(event, siteOrigin = SITE_ORIGIN) {
  const crumb = (position, name, item) => ({ "@type": "ListItem", position, name, item });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      crumb(1, "Home", siteOrigin),
      crumb(2, "Events", `${siteOrigin}/events`),
      crumb(3, event.title, eventUrl(event.slug, siteOrigin)),
    ],
  };
}

/**
 * `robots` obeys two independent rules: the launch flag (nothing on /events is indexable before the owner
 * releases it) and the event's own `seo.noindex`, which is true for every unlisted event and stays true after
 * release. An ended or unknown event is always noindex: `state` carries that.
 */
export function buildEventHead({ event, state = "live", siteOrigin = SITE_ORIGIN, now = new Date() } = {}) {
  if (!event) {
    const title = state === "gone" ? "This event has ended | KGOB CPAs & Advisors" : "Event not found | KGOB CPAs & Advisors";
    const description = state === "gone"
      ? "This event is no longer scheduled. See what else KGOB CPAs & Advisors has coming up."
      : "We could not find that event. See what KGOB CPAs & Advisors has coming up.";
    return {
      title, description, canonical: `${siteOrigin}/events`, robots: "noindex, follow",
      og: { title, description, url: `${siteOrigin}/events`, type: "website", siteName: "KGOB CPAs & Advisors", image: fallbackCard(siteOrigin) },
      twitter: { card: "summary_large_image", title, description, image: fallbackCard(siteOrigin).url },
      jsonLd: [],
    };
  }
  const img = event.images?.og ?? event.images?.hero ?? null;
  // An event with no artwork gets the neutral KGOB events card, never the landing's Belmont photograph: a
  // seminar that shares as a picture of a different event is worse than one that shares as a wordmark
  // (the app agent's YOUR-EVENT-PAGE-REVIEWED-AND-THE-LIST-GAP.md §8).
  const image = img?.url ? { url: absolute(img.url, siteOrigin), alt: img.alt || event.title } : fallbackCard(siteOrigin);
  const noindex = state !== "live" || event.visibility === "unlisted" || event.seo?.noindex === true;
  const title = event.seo?.title || `${event.title} | KGOB`;
  const description = event.seo?.description || event.summary || LANDING.description;
  const sessions = Array.isArray(event.event_sessions) ? event.event_sessions : [];
  return {
    title,
    description,
    canonical: eventUrl(event.slug, siteOrigin),
    robots: noindex ? "noindex, follow" : (RELEASED ? "index, follow" : "noindex, follow"),
    og: { title: event.title, description: event.summary || description, url: eventUrl(event.slug, siteOrigin), type: "website", siteName: "KGOB CPAs & Advisors", image },
    twitter: { card: "summary_large_image", title: event.title, description: event.summary || description, image: image.url },
    jsonLd: [
      ...sessions.map((s) => {
        const node = eventJsonLd(event, s, siteOrigin, now);
        if (!node.image) return node;
        return { ...node, image: Array.isArray(node.image) ? (node.image || []).map((u) => absolute(u, siteOrigin)) : absolute(node.image, siteOrigin) };
      }),
      breadcrumbJsonLd(event, siteOrigin),
    ],
  };
}

/** Is this event allowed into sitemap.xml? Public, live, and not noindex — the launch flag is applied by the generator. */
export const inSitemap = (event) =>
  !!event && event.visibility === "public" && event.seo?.noindex !== true && event.status !== "withdrawn";
